import { type UseMutationResult, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useGlobalStore } from '../../store/global-store/global-store';
import {
  createAutomationWorkflow,
  deleteAutomationWorkflow,
  getAutomationEmailRecipientSuggestions,
  getAutomationFilterDefinitions,
  getAutomationWorkflow,
  getAutomationWorkflowRuns,
  getAutomationWorkflows,
  type RunAutomationWorkflowResponse,
  runAutomationWorkflow,
  updateAutomationWorkflow,
} from './automations-workflows-client';
import type {
  AutomationWorkflow,
  AutomationWorkflowWrite,
  UseAutomationEmailRecipientSuggestionsResult,
  UseAutomationFilterDefinitionsResult,
  UseAutomationWorkflowResult,
  UseAutomationWorkflowRunsResult,
  UseAutomationWorkflowsResult,
} from './automations-workflows-client.type';
import { AutomationRunStatus } from './automations-workflows-client.type';

const AUTOMATION_WORKFLOWS_KEY = 'automation-workflows';
const AUTOMATION_FILTER_DEFINITIONS_KEY = 'automation-filter-definitions';
const AUTOMATION_EMAIL_RECIPIENT_SUGGESTIONS_KEY = 'automation-email-recipient-suggestions';
const AUTOMATION_RUNS_POLL_INTERVAL_MS = 2_000;

/**
 * React Query key for a single workflow's run history. Exported so callers
 * outside the runs view (e.g. the list view's "Run now") can invalidate or
 * drop the cached runs for a workflow without duplicating the key shape.
 *
 * @param {string | undefined} workflowId - The workflow UUID
 * @returns {(string | undefined)[]} The query key for that workflow's runs
 */
const automationWorkflowRunsKey = (workflowId: string | undefined): (string | undefined)[] => [
  AUTOMATION_WORKFLOWS_KEY,
  workflowId,
  'runs',
];
const FILTER_DEFINITIONS_STALE_MS = 5 * 60 * 1000;
const EMAIL_RECIPIENT_SUGGESTIONS_STALE_MS = 60 * 1000;

/**
 * Hook to fetch all automation workflows for the active organization.
 *
 * @returns {UseAutomationWorkflowsResult} Object containing workflows, loading state, error, and refetch
 */
const useAutomationWorkflows = (): UseAutomationWorkflowsResult => {
  const organizationId = useGlobalStore((state) => state.organization?.id);
  const query = useQuery({
    queryKey: [AUTOMATION_WORKFLOWS_KEY, organizationId],
    queryFn: () => {
      if (!organizationId) throw new Error('Organization ID required');
      return getAutomationWorkflows(organizationId);
    },
    enabled: !!organizationId,
  });

  return {
    workflows: query.data || [],
    isLoading: query.isLoading,
    error: query.error,
    refetch: query.refetch,
  };
};

/**
 * Hook to fetch a single automation workflow by id.
 *
 * @param {string | undefined} id - The workflow UUID, or undefined to skip the fetch
 * @returns {UseAutomationWorkflowResult} Object containing the workflow, loading state, error, and refetch
 */
const useAutomationWorkflow = (id: string | undefined): UseAutomationWorkflowResult => {
  const query = useQuery({
    queryKey: [AUTOMATION_WORKFLOWS_KEY, 'detail', id],
    queryFn: () => {
      if (!id) throw new Error('Automation workflow ID required');
      return getAutomationWorkflow(id);
    },
    enabled: !!id,
    // A workflow the user doesn't own (or a deleted one) returns a deterministic
    // 404 — retrying only delays the not-found state behind a spinner.
    retry: false,
  });

  return {
    workflow: query.data,
    isLoading: query.isLoading,
    error: query.error,
    refetch: query.refetch,
  };
};

/**
 * Hook to create a new automation workflow. Invalidates the workflows list cache on success.
 *
 * @returns {UseMutationResult<AutomationWorkflow, Error, AutomationWorkflowWrite, unknown>} React Query mutation
 */
const useCreateAutomationWorkflow = (): UseMutationResult<
  AutomationWorkflow,
  Error,
  AutomationWorkflowWrite,
  unknown
> => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: AutomationWorkflowWrite) => createAutomationWorkflow(data),
    onSuccess: () => {
      // The app sets `refetchOnMount: false` globally, so just invalidating
      // doesn't trigger a refetch when the list view next mounts. Explicitly
      // refetch so the navigation back from the builder shows the latest list.
      queryClient.refetchQueries({ queryKey: [AUTOMATION_WORKFLOWS_KEY] });
    },
  });
};

/**
 * Hook to partially update an existing automation workflow.
 *
 * @returns {UseMutationResult<AutomationWorkflow, Error, { id: string; data: Partial<AutomationWorkflowWrite> }, unknown>} React Query mutation
 */
const useUpdateAutomationWorkflow = (): UseMutationResult<
  AutomationWorkflow,
  Error,
  { id: string; data: Partial<AutomationWorkflowWrite> },
  unknown
> => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<AutomationWorkflowWrite> }) =>
      updateAutomationWorkflow(id, data),
    onSuccess: () => {
      // The app sets `refetchOnMount: false` globally, so just invalidating
      // doesn't trigger a refetch when the list view next mounts. Explicitly
      // refetch so the navigation back from the builder shows the latest list.
      queryClient.refetchQueries({ queryKey: [AUTOMATION_WORKFLOWS_KEY] });
    },
  });
};

/**
 * Hook to delete an automation workflow.
 *
 * @returns {UseMutationResult<void, Error, string, unknown>} React Query mutation taking the workflow id
 */
const useDeleteAutomationWorkflow = (): UseMutationResult<void, Error, string, unknown> => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => deleteAutomationWorkflow(id),
    onSuccess: () => {
      // The app sets `refetchOnMount: false` globally, so just invalidating
      // doesn't trigger a refetch when the list view next mounts. Explicitly
      // refetch so the navigation back from the builder shows the latest list.
      queryClient.refetchQueries({ queryKey: [AUTOMATION_WORKFLOWS_KEY] });
    },
  });
};

/**
 * Hook to trigger a manual run of an automation workflow. Invalidates the
 * triggering workflow's run history and the workflows list so a freshly queued
 * run and the updated last-run badge appear without a hard refresh.
 *
 * @returns {UseMutationResult<RunAutomationWorkflowResponse, Error, string, unknown>} React Query mutation taking the workflow id
 */
const useRunAutomationWorkflow = (): UseMutationResult<RunAutomationWorkflowResponse, Error, string, unknown> => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => runAutomationWorkflow(id),
    onSuccess: (_data, id) => {
      // Refresh only the triggering workflow's run history so the freshly
      // queued run appears. When the runs view is mounted (run triggered from
      // there) this refetches in place; when triggered from the list view the
      // query is inactive, so the list view additionally drops the cache (see
      // its handleRunNow) to defeat the global refetchOnMount: false.
      queryClient.invalidateQueries({ queryKey: automationWorkflowRunsKey(id) });
    },
  });
};

/**
 * Hook to fetch the run history for a single automation workflow.
 *
 * @param {string | undefined} workflowId - The workflow UUID, or undefined to skip the fetch
 * @returns {UseAutomationWorkflowRunsResult} Object containing runs, loading/fetching state, error, and refetch
 */
const useAutomationWorkflowRuns = (workflowId: string | undefined): UseAutomationWorkflowRunsResult => {
  const query = useQuery({
    queryKey: automationWorkflowRunsKey(workflowId),
    queryFn: () => {
      if (!workflowId) throw new Error('Automation workflow ID required');
      return getAutomationWorkflowRuns(workflowId);
    },
    enabled: !!workflowId,
    // A workflow the user doesn't own (or a deleted one) returns a deterministic
    // 404 — retrying only delays the not-found state behind a spinner.
    retry: false,
    // A run is created asynchronously. Keep the history current while any run
    // is still queued or executing, then stop polling as soon as all runs reach
    // a terminal state. This lets a newly triggered run update in place without
    // continuously polling completed histories.
    refetchInterval: (currentQuery) =>
      currentQuery.state.data?.some(
        (run) => run.status === AutomationRunStatus.PENDING || run.status === AutomationRunStatus.PROCESSING,
      )
        ? AUTOMATION_RUNS_POLL_INTERVAL_MS
        : false,
  });

  return {
    runs: query.data ?? [],
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    error: query.error,
    refetch: query.refetch,
  };
};

/**
 * Hook to fetch the event/filter definition catalog used to drive the trigger picker. Cached for 5 minutes since the catalog is effectively static.
 *
 * @returns {UseAutomationFilterDefinitionsResult} Object containing the event definitions, loading state, and error
 */
const useAutomationFilterDefinitions = (): UseAutomationFilterDefinitionsResult => {
  const organizationId = useGlobalStore((state) => state.organization?.id);
  const query = useQuery({
    // Keyed by org so switching organizations refetches — the catalog can
    // differ per org (org-restricted internal triggers like Flora Debug Error).
    queryKey: [AUTOMATION_FILTER_DEFINITIONS_KEY, organizationId],
    queryFn: () => {
      if (!organizationId) throw new Error('Organization ID required');
      return getAutomationFilterDefinitions(organizationId);
    },
    enabled: !!organizationId,
    staleTime: FILTER_DEFINITIONS_STALE_MS,
  });

  return {
    definitions: query.data?.events ?? [],
    isLoading: query.isLoading,
    error: query.error,
  };
};

/**
 * Hook to search active members of the current organization for email recipients.
 *
 * @param {string} search - Case-insensitive name or email search text
 * @param {boolean} enabled - Whether the recipient picker is currently active
 * @returns {UseAutomationEmailRecipientSuggestionsResult} Suggestions plus query state
 */
const useAutomationEmailRecipientSuggestions = (
  search: string,
  enabled = true,
): UseAutomationEmailRecipientSuggestionsResult => {
  const organizationId = useGlobalStore((state) => state.organization?.id);
  const normalizedSearch = search.trim();
  const query = useQuery({
    queryKey: [AUTOMATION_EMAIL_RECIPIENT_SUGGESTIONS_KEY, organizationId, normalizedSearch],
    queryFn: () => {
      if (!organizationId) throw new Error('Organization ID required');
      return getAutomationEmailRecipientSuggestions(organizationId, normalizedSearch);
    },
    enabled: enabled && !!organizationId && normalizedSearch.length > 0,
    staleTime: EMAIL_RECIPIENT_SUGGESTIONS_STALE_MS,
  });

  return {
    suggestions: normalizedSearch ? (query.data?.results ?? []) : [],
    isLoading: query.isLoading,
    error: query.error,
  };
};

export {
  automationWorkflowRunsKey,
  useAutomationEmailRecipientSuggestions,
  useAutomationFilterDefinitions,
  useAutomationWorkflow,
  useAutomationWorkflowRuns,
  useAutomationWorkflows,
  useCreateAutomationWorkflow,
  useDeleteAutomationWorkflow,
  useRunAutomationWorkflow,
  useUpdateAutomationWorkflow,
};
